import React from 'react';
import { motion } from 'framer-motion';

export const PrismHero = () => {
  return (
    <div className="relative w-full h-[600px] flex items-center justify-center overflow-visible perspective-1000">
      {/* 1. Ambient Environment / Grid / HUD - With Breathing */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        animate={{ scale: [1, 1.02, 1], opacity: [0.8, 1, 0.8] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* Radial Grid */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-glass-border/20 rounded-full opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-glass-border/20 rounded-full opacity-30" />
        
        {/* Rotating Dashed Ring */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-glass-cyan/10 rounded-full opacity-40 border-dashed"
          animate={{ rotate: 360 }}
          transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Crosshairs */}
        <div className="absolute top-0 left-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-glass-border/30 to-transparent" />
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-glass-border/30 to-transparent" />
      </motion.div>

      {/* 2. The Optical Assembly (The Engine) */}
      <div className="relative z-10 w-96 h-96 flex items-center justify-center">
        
        {/* Outer Ring (Rotating) */}
        <motion.div 
          className="absolute inset-0 border border-glass-border/30 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-glass-cyan/50 rounded-full blur-[1px]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-glass-cyan/50 rounded-full blur-[1px]" />
        </motion.div>

        {/* Inner Ring (Counter-Rotating) */}
        <motion.div 
          className="absolute inset-12 border border-glass-cyan/20 rounded-full border-t-transparent border-l-transparent"
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        />

        {/* The Crystal Core (Complex Geometry) - With Pulse */}
        <motion.div 
          className="relative w-48 h-48"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Layer 1: Base Prism */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-md border border-white/10 transform rotate-45 z-20 shadow-[0_0_30px_rgba(0,240,255,0.1)]"
               style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}>
             {/* Internal Refraction Lines */}
             <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_45%,rgba(255,255,255,0.1)_50%,transparent_55%)] bg-[length:10px_10px]" />
          </div>

          {/* Layer 2: Inner Facet (Floating) */}
          <motion.div 
            className="absolute inset-8 bg-glass-cyan/10 backdrop-blur-xl border border-glass-cyan/30 z-30"
            animate={{ 
              rotate: [45, 225],
              scale: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 10, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-glass-cyan/20 to-transparent" />
          </motion.div>

          {/* Layer 3: Core Energy Source (Breathing Heart) */}
          <motion.div 
            className="absolute inset-[40%] bg-white rounded-full blur-md z-40 mix-blend-screen"
            animate={{ 
              opacity: [0.4, 0.8, 0.4],
              scale: [1, 1.3, 1]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </div>

      {/* 3. Data Streams (The Routing) */}
      
      {/* Input Stream (High Velocity) */}
      <div className="absolute left-0 top-1/2 w-[calc(50%-6rem)] h-[2px] overflow-hidden z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-white blur-[1px]" />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent w-[50%]"
          animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        {/* Particles */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`in-${i}`}
            className="absolute top-1/2 w-8 h-[2px] bg-glass-cyan"
            animate={{ x: ['-100%', '400%'] }}
            transition={{ 
              duration: 0.5, 
              repeat: Infinity, 
              delay: i * 0.2, 
              ease: "linear" 
            }}
          />
        ))}
      </div>

      {/* Output Streams (Divergent) */}
      
      {/* Stream A: Text (Solid, Stable) */}
      <div className="absolute right-0 top-1/2 w-[calc(50%-6rem)] h-[1px] origin-left rotate-[-15deg] z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-glass-cyan via-glass-cyan/50 to-transparent opacity-50" />
        <motion.div 
          className="absolute top-0 left-0 w-2 h-2 bg-white rounded-full blur-[2px]"
          animate={{ x: [0, 300], opacity: [1, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
        />
        <div className="absolute right-10 -top-4 text-[10px] font-mono text-glass-cyan/70 tracking-widest">TEXT_MODEL_01</div>
      </div>

      {/* Stream B: Image (Refined - Fiber Optic Bundle) */}
      <div className="absolute right-0 top-1/2 w-[calc(50%-6rem)] h-[6px] origin-left rotate-[15deg] z-0 flex flex-col justify-between opacity-80">
        {/* Three parallel fine lines instead of one block */}
        {[0, 1, 2].map((i) => (
          <div key={i} className="relative w-full h-[1px] overflow-hidden">
             <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-blue-400/50 to-transparent opacity-30" />
             <motion.div 
               className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent w-[30%]"
               animate={{ x: ['-100%', '400%'] }}
               transition={{ 
                 duration: 2 + i * 0.5, 
                 repeat: Infinity, 
                 ease: "linear",
                 delay: i * 0.3
               }}
             />
          </div>
        ))}
        <div className="absolute right-10 top-4 text-[10px] font-mono text-blue-400/70 tracking-widest">IMG_GEN_V2</div>
      </div>

      {/* Stream C: Video (Data Packets) */}
      <div className="absolute right-0 top-1/2 w-[calc(50%-6rem)] h-[1px] origin-left rotate-[35deg] z-0">
        <div className="absolute inset-0 border-t border-dashed border-purple-400/30" />
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`out-${i}`}
            className="absolute top-[-1px] w-4 h-[2px] bg-purple-400 shadow-[0_0_5px_purple]"
            animate={{ x: [0, 400], opacity: [1, 0] }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              delay: i * 0.5, 
              ease: "linear" 
            }}
          />
        ))}
        <div className="absolute right-20 top-2 text-[10px] font-mono text-purple-400/70 tracking-widest">VIDEO_STREAM</div>
      </div>

      {/* 4. Lens Flares / Optical Artifacts - With Breathing */}
      <div className="absolute inset-0 pointer-events-none mix-blend-screen">
        <motion.div 
          className="absolute top-1/2 left-1/2 w-[800px] h-[100px] bg-glass-cyan/10 blur-[100px] -translate-x-1/2 -translate-y-1/2"
          animate={{ opacity: [0.2, 0.4, 0.2], scale: [1, 1.1, 1] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

    </div>
  );
};
